/**
 * ChainVault WP - Settings Page
 *
 * User-facing settings including account management,
 * security, preferences and wallet info.
 *
 * @package ChainVault
 * @since   1.0.0
 */

import React, { useState, useEffect } from 'react';
import { useWallet } from '../context/WalletContext';
import ReferralPage from './ReferralPage';
import toast from 'react-hot-toast';
import { deriveEthWallet, deriveSolWallet } from '../utils/wallet';

// Derive additional accounts using same custom derivation as main wallet
// Appends account index to mnemonic so each account gets a unique but
// deterministic key pair derived from the same seed phrase
async function deriveAccount( mnemonic, index ) {
    const accountMnemonic = index === 0 ? mnemonic : mnemonic + ' account' + index;
    const ethWallet = await deriveEthWallet( accountMnemonic );
    const solWallet = await deriveSolWallet( accountMnemonic );
    return {
        index,
        label:           'Account ' + ( index + 1 ),
        ethereumAddress: ethWallet.address,
        privateKey:      ethWallet.privateKey,
        solanaAddress:   solWallet.address,
    };
}

function SettingsPage() {

    const {
        walletType,
        builtInWallet,
        address,
        selectedNetwork,
        disconnectWallet,
        saveAccountCount,
        switchAccount,
        theme,
        toggleTheme,
        formatAddress,
        testnetMode,
        toggleTestnetMode,
    } = useWallet();

    const [ activeSection,   setActiveSection   ] = useState( 'accounts' );
    const [ showSeedPhrase,  setShowSeedPhrase  ] = useState( false );
    const [ seedPassword,    setSeedPassword    ] = useState( '' );
    const [ seedRevealed,    setSeedRevealed    ] = useState( false );
    const [ accounts,        setAccounts        ] = useState( [] );
    const [ activeAccount,   setActiveAccount   ] = useState( 0 );
    const [ isAddingAccount, setIsAddingAccount ] = useState( false );
    const [ hiddenAssets, setHiddenAssets ] = useState( () => {
        try { return JSON.parse( localStorage.getItem( 'chainvault_hidden_assets' ) || '[]' ); } catch(e) { return []; }
    } );

    const refreshHiddenAssets = () => {
        try { setHiddenAssets( JSON.parse( localStorage.getItem( 'chainvault_hidden_assets' ) || '[]' ) ); } catch(e) { setHiddenAssets( [] ); }
    };

    const unhideAsset = ( id ) => {
        const updated = hiddenAssets.filter( h => h.id !== id );
        localStorage.setItem( 'chainvault_hidden_assets', JSON.stringify( updated ) );
        setHiddenAssets( updated );
        window.dispatchEvent( new Event( 'chainvault_tokens_updated' ) );
    };

    useEffect( () => {
        window.addEventListener( 'chainvault_tokens_updated', refreshHiddenAssets );
        return () => window.removeEventListener( 'chainvault_tokens_updated', refreshHiddenAssets );
    }, [] );

    const mnemonic = builtInWallet?.mnemonic || null;

    // Load accounts from localStorage on mount
    const loadAccountsFromStorage = () => {
        const stored = localStorage.getItem( 'chainvault_accounts' );
        if ( stored ) {
            try { setAccounts( JSON.parse( stored ) ); } catch ( e ) {}
        } else if ( builtInWallet ) {
            const acc1 = {
                index:           0,
                label:           'Account 1',
                ethereumAddress: builtInWallet.ethereum.address,
                solanaAddress:   builtInWallet.solana.address,
            };
            setAccounts( [ acc1 ] );
            localStorage.setItem( 'chainvault_accounts', JSON.stringify( [ acc1 ] ) );
        }
        const activeIdx = parseInt( localStorage.getItem( 'chainvault_active_account' ) || '0' );
        setActiveAccount( activeIdx );
    };

    useEffect( () => {
        loadAccountsFromStorage();
        /* Re-read after longer delays to catch accounts restored from server (mobile needs more time) */
        const t = setTimeout( loadAccountsFromStorage, 3000 );
        /* Re-read after 4s as fallback */
        const t2 = setTimeout( loadAccountsFromStorage, 7000 );
        return () => { clearTimeout(t); clearTimeout(t2); };
    }, [ builtInWallet ] );

    const handleAddAccount = async () => {
        if ( ! mnemonic ) {
            toast.error( 'Only available for built-in wallets.' );
            return;
        }
        setIsAddingAccount( true );
        try {
            const newIndex   = accounts.length;
            const derived    = await deriveAccount( mnemonic, newIndex );
            const newAccount = {
                index:           newIndex,
                label:           'Account ' + ( newIndex + 1 ),
                ethereumAddress: derived.ethereumAddress,
                solanaAddress:   derived.solanaAddress,
            };
            const updated = [ ...accounts, newAccount ];
            setAccounts( updated );
            localStorage.setItem( 'chainvault_accounts', JSON.stringify( updated ) );
            toast.success( 'Account ' + ( newIndex + 1 ) + ' created!' );
            /* Save count to server so accounts survive cache clear */
            if ( saveAccountCount && builtInWallet?.ethereum?.address ) {
                saveAccountCount( builtInWallet.ethereum.address, updated.length );
            }
        } catch ( err ) {
            toast.error( 'Failed to create account.' );
        } finally {
            setIsAddingAccount( false );
        }
    };

    const handleSwitchAccount = async ( index ) => {
        setActiveAccount( index );
        localStorage.setItem( 'chainvault_active_account', index.toString() );
        // Switch globally so navbar and wallet page update
        if ( accounts[ index ] ) {
            await switchAccount( accounts[ index ] );
        }
    };

    const handleRevealSeedPhrase = () => {
        if ( seedPassword === 'confirm' ) {
            setSeedRevealed( true );
        } else {
            toast.error( 'Type "confirm" to reveal your seed phrase.' );
        }
    };

    const handleCopyAddress = ( addr, label ) => {
        navigator.clipboard.writeText( addr );
        toast.success( label + ' address copied!' );
    };

    const handleCopySeed = () => {
        if ( mnemonic ) {
            navigator.clipboard.writeText( mnemonic );
            toast.success( 'Seed phrase copied! Keep it safe.' );
        }
    };

    const sections = [
        { id: 'accounts',    label: 'Accounts',          icon: '👤' },
        { id: 'security',    label: 'Security',           icon: '🔒' },
        { id: 'addresses',   label: 'My Addresses',       icon: '📋' },
        { id: 'preferences', label: 'Preferences',        icon: '⚙️' },
        { id: 'network',      label: 'Network',             icon: '🌐' },
        { id: 'assets',      label: 'Manage Assets',       icon: '🪙' },
        { id: 'about',       label: 'About ' + (window.chainvaultSettings?.branding?.app_name||'ChainVault'), icon: 'ℹ️' },
        ...( window.chainvaultSettings?.branding?.referral_enabled === '1' ? [{ id: 'referral', label: 'Referrals', icon: '🔗' }] : [] ),
    ];

    const renderSection = () => {
        switch ( activeSection ) {

            case 'accounts': return (
                <div className="cv-settings-content">
                    <h2>Manage Accounts</h2>
                    <p style={{ color: '#888', fontSize: '14px', marginBottom: '20px' }}>
                        Create and switch between multiple wallet accounts derived from the same seed phrase.
                    </p>

                    { accounts.map( ( acc, i ) => (
                        <div key={ i } className="cv-settings-account-row" style={{
                            display:        'flex',
                            alignItems:     'center',
                            justifyContent: 'space-between',
                            background:     activeAccount === i ? 'rgba(108,99,255,0.12)' : 'rgba(255,255,255,0.04)',
                            border:         activeAccount === i ? '1px solid rgba(108,99,255,0.4)' : '1px solid rgba(255,255,255,0.08)',
                            borderRadius:   '12px',
                            padding:        '14px 16px',
                            marginBottom:   '10px',
                            cursor:         'pointer',
                            transition:     'all 0.2s',
                        }} onClick={ () => handleSwitchAccount( i ) }>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width:        '38px',
                                    height:       '38px',
                                    borderRadius: '50%',
                                    background:   activeAccount === i ? 'var(--cv-primary)' : 'rgba(255,255,255,0.1)',
                                    display:      'flex',
                                    alignItems:   'center',
                                    justifyContent: 'center',
                                    fontWeight:   '700',
                                    fontSize:     '14px',
                                    color:        '#fff',
                                }}>
                                    { acc.label.charAt( 0 ) }{ i + 1 }
                                </div>
                                <div>
                                    <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--cv-text)' }}>
                                        { acc.label }
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                                        { formatAddress( acc.ethereumAddress ) }
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                { activeAccount === i && (
                                    <span style={{ fontSize: '11px', color: 'var(--cv-primary)', fontWeight: '600', background: 'rgba(108,99,255,0.15)', padding: '2px 8px', borderRadius: '20px' }}>
                                        Active
                                    </span>
                                ) }
                                <button
                                    onClick={ e => { e.stopPropagation(); handleCopyAddress( acc.ethereumAddress, acc.label ); } }
                                    style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '14px' }}
                                    title="Copy address"
                                >
                                    📋
                                </button>
                            </div>
                        </div>
                    ) ) }

                    { walletType === 'builtin' && (
                        <button
                            className="cv-btn-secondary"
                            onClick={ handleAddAccount }
                            disabled={ isAddingAccount }
                            style={{ width: '100%', marginTop: '8px', padding: '12px' }}
                        >
                            { isAddingAccount ? 'Creating...' : '+ Add Account' }
                        </button>
                    ) }

                    { walletType !== 'builtin' && (
                        <div style={{ padding: '16px', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', marginTop: '12px' }}>
                            <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>
                                Multiple accounts are only available for built-in ChainVault wallets.
                                Connect or create a built-in wallet to use this feature.
                            </p>
                        </div>
                    ) }
                </div>
            );

            case 'security': return (
                <div className="cv-settings-content">
                    <h2>Security</h2>

                    { /* Seed Phrase */ }
                    <div className="cv-settings-card">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                            <span style={{ fontSize: '24px' }}>🌱</span>
                            <div>
                                <div style={{ fontWeight: '600', fontSize: '15px' }}>Secret Recovery Phrase</div>
                                <div style={{ fontSize: '12px', color: '#888' }}>12-word seed phrase for your wallet</div>
                            </div>
                        </div>

                        { ! mnemonic ? (
                            <p style={{ color: '#888', fontSize: '13px' }}>
                                Only available for built-in ChainVault wallets.
                            </p>
                        ) : ! seedRevealed ? (
                            <>
                                <div style={{
                                    background:   'rgba(255,100,100,0.08)',
                                    border:       '1px solid rgba(255,100,100,0.2)',
                                    borderRadius: '8px',
                                    padding:      '12px',
                                    marginBottom: '12px',
                                    fontSize:     '13px',
                                    color:        '#ff8888',
                                }}>
                                    ⚠️ Never share your seed phrase with anyone. Anyone with this phrase can access your wallet.
                                </div>
                                <input
                                    type="text"
                                    className="cv-input"
                                    placeholder='Type "confirm" to reveal'
                                    value={ seedPassword }
                                    onChange={ e => setSeedPassword( e.target.value ) }
                                    style={{ marginBottom: '10px' }}
                                />
                                <button className="cv-btn-primary full-width" onClick={ handleRevealSeedPhrase }>
                                    Reveal Seed Phrase
                                </button>
                            </>
                        ) : (
                            <>
                                <div style={{
                                    display:             'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap:                 '8px',
                                    marginBottom:        '12px',
                                }}>
                                    { mnemonic.split( ' ' ).map( ( word, i ) => (
                                        <div key={ i } style={{
                                            background:   'rgba(255,255,255,0.06)',
                                            borderRadius: '8px',
                                            padding:      '8px 10px',
                                            fontSize:     '13px',
                                            display:      'flex',
                                            gap:          '8px',
                                            alignItems:   'center',
                                        }}>
                                            <span style={{ color: '#888', fontSize: '11px', minWidth: '16px' }}>{ i + 1 }</span>
                                            <span style={{ fontWeight: '600', color: 'var(--cv-text)' }}>{ word }</span>
                                        </div>
                                    ) ) }
                                </div>
                                <button className="cv-btn-secondary full-width" onClick={ handleCopySeed }>
                                    📋 Copy Seed Phrase
                                </button>
                                <button
                                    onClick={ () => { setSeedRevealed( false ); setSeedPassword( '' ); } }
                                    style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '13px', marginTop: '8px', width: '100%' }}
                                >
                                    Hide Seed Phrase
                                </button>
                            </>
                        ) }
                    </div>

                    { /* Disconnect */ }
                    <div className="cv-settings-card" style={{ marginTop: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                            <span style={{ fontSize: '24px' }}>🔌</span>
                            <div>
                                <div style={{ fontWeight: '600', fontSize: '15px' }}>Disconnect Wallet</div>
                                <div style={{ fontSize: '12px', color: '#888' }}>Remove wallet from this browser</div>
                            </div>
                        </div>
                        <button
                            onClick={ disconnectWallet }
                            style={{
                                width:        '100%',
                                padding:      '10px',
                                background:   'rgba(255,77,109,0.1)',
                                border:       '1px solid rgba(255,77,109,0.3)',
                                borderRadius: '8px',
                                color:        '#ff4d6d',
                                cursor:       'pointer',
                                fontSize:     '14px',
                                fontWeight:   '600',
                            }}
                        >
                            Disconnect Wallet
                        </button>
                    </div>
                </div>
            );

            case 'addresses': return (
                <div className="cv-settings-content">
                    <h2>My Addresses</h2>
                    <p style={{ color: '#888', fontSize: '14px', marginBottom: '20px' }}>
                        Your wallet addresses for each network. Share these to receive crypto.
                    </p>

                    { [
                        {
                            label:   'Ethereum / EVM',
                            icon:    '⟠',
                            address: walletType === 'builtin' && builtInWallet ? builtInWallet.ethereum.address : address,
                            color:   '#627EEA',
                            chains:  'Ethereum, BNB Chain, Polygon, Base',
                        },
                        {
                            label:   'Solana',
                            icon:    '◎',
                            address: walletType === 'builtin' && builtInWallet ? builtInWallet.solana.address : ( selectedNetwork === 'solana' ? address : null ),
                            color:   '#9945FF',
                            chains:  'Solana mainnet',
                        },
                        {
                            label:   'Bitcoin',
                            icon:    '₿',
                            address: walletType === 'builtin' && builtInWallet ? builtInWallet.bitcoin?.address : null,
                            color:   '#F7931A',
                            chains:  'Bitcoin mainnet',
                        },
                    ].map( ( item, i ) => (
                        <div key={ i } className="cv-settings-card" style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                <span style={{ fontSize: '20px', color: item.color }}>{ item.icon }</span>
                                <div>
                                    <div style={{ fontWeight: '600', fontSize: '14px' }}>{ item.label }</div>
                                    <div style={{ fontSize: '11px', color: '#888' }}>{ item.chains }</div>
                                </div>
                            </div>
                            { item.address ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <code style={{
                                        flex:         1,
                                        background:   'rgba(255,255,255,0.04)',
                                        padding:      '8px 10px',
                                        borderRadius: '6px',
                                        fontSize:     '12px',
                                        color:        '#ccc',
                                        wordBreak:    'break-all',
                                    }}>
                                        { item.address }
                                    </code>
                                    <button
                                        onClick={ () => handleCopyAddress( item.address, item.label ) }
                                        className="cv-btn-secondary"
                                        style={{ padding: '8px 12px', flexShrink: 0 }}
                                    >
                                        Copy
                                    </button>
                                </div>
                            ) : (
                                <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>
                                    Not available for this wallet type.
                                </p>
                            ) }
                        </div>
                    ) ) }
                </div>
            );

            case 'preferences': return (
                <div className="cv-settings-content">
                    <h2>Preferences</h2>

                    <div className="cv-settings-card">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '20px' }}>{ theme === 'dark' ? '🌙' : '☀️' }</span>
                                <div>
                                    <div style={{ fontWeight: '600', fontSize: '14px' }}>Theme</div>
                                    <div style={{ fontSize: '12px', color: '#888' }}>{ theme === 'dark' ? 'Dark mode' : 'Light mode' }</div>
                                </div>
                            </div>
                            <button
                                onClick={ toggleTheme }
                                style={{
                                    background:   theme === 'dark' ? 'rgba(108,99,255,0.2)' : 'rgba(255,193,7,0.2)',
                                    border:       '1px solid ' + ( theme === 'dark' ? 'rgba(108,99,255,0.4)' : 'rgba(255,193,7,0.4)' ),
                                    borderRadius: '20px',
                                    padding:      '6px 16px',
                                    color:        theme === 'dark' ? 'var(--cv-primary)' : '#ffc107',
                                    cursor:       'pointer',
                                    fontSize:     '13px',
                                    fontWeight:   '600',
                                }}
                            >
                                { theme === 'dark' ? 'Switch to Light' : 'Switch to Dark' }
                            </button>
                        </div>
                    </div>

                    <div className="cv-settings-card" style={{ marginTop: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '20px' }}>🔔</span>
                            <div>
                                <div style={{ fontWeight: '600', fontSize: '14px' }}>Sound & Voice</div>
                                <div style={{ fontSize: '12px', color: '#888' }}>Receive sounds and "Excuse me Boss" voice alerts are enabled</div>
                            </div>
                        </div>
                    </div>

                    <div className="cv-settings-card" style={{ marginTop: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '20px' }}>🌐</span>
                            <div>
                                <div style={{ fontWeight: '600', fontSize: '14px' }}>Currency</div>
                                <div style={{ fontSize: '12px', color: '#888' }}>USD — United States Dollar</div>
                            </div>
                        </div>
                    </div>
                </div>
            );

            case 'network': return (
                <div className="cv-settings-content">
                    <h2>Network Settings</h2>
                    <p style={{ color: '#888', fontSize: '14px', marginBottom: '24px' }}>
                        Switch between mainnet and testnet across all networks simultaneously.
                    </p>

                    { /* Testnet Mode Toggle */ }
                    <div style={{ background: testnetMode ? 'rgba(255,170,51,0.08)' : 'rgba(255,255,255,0.03)', border: '1px solid ' + (testnetMode ? 'rgba(255,170,51,0.3)' : 'rgba(255,255,255,0.08)'), borderRadius: '16px', padding: '20px', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <div>
                                <div style={{ fontWeight: '700', color: '#fff', fontSize: '15px', marginBottom: '4px' }}>
                                    { testnetMode ? '🧪 Testnet Mode' : '🌐 Mainnet Mode' }
                                </div>
                                <div style={{ fontSize: '13px', color: '#888' }}>
                                    { testnetMode ? 'Using test networks — no real funds involved' : 'Using live networks with real funds' }
                                </div>
                            </div>
                            <label className="cv-toggle-switch">
                                <input type="checkbox" checked={ testnetMode } onChange={ toggleTestnetMode } />
                                <span className="cv-toggle-slider-round"></span>
                            </label>
                        </div>
                        { testnetMode && (
                            <div style={{ background: 'rgba(255,170,51,0.06)', borderRadius: '10px', padding: '12px', fontSize: '12px', color: '#ffaa33' }}>
                                ⚠️ Testnet mode active. Tokens and transactions have no real value.
                            </div>
                        ) }
                    </div>

                    { /* Network Table */ }
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', overflow: 'hidden' }}>
                        { [
                            { name: 'Ethereum', mainnet: 'Mainnet',      testnet: 'Sepolia',      color: '#627EEA' },
                            { name: 'BSC',      mainnet: 'Mainnet',      testnet: 'BSC Testnet',  color: '#F0B90B' },
                            { name: 'Polygon',  mainnet: 'Mainnet',      testnet: 'Amoy',         color: '#8247E5' },
                            { name: 'Base',     mainnet: 'Mainnet',      testnet: 'Base Sepolia', color: '#0052FF' },
                            { name: 'Solana',   mainnet: 'Mainnet',      testnet: 'Devnet',       color: '#9945FF' },
                        ].map( ( net, i ) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: net.color }} />
                                    <span style={{ fontWeight: '600', color: '#fff', fontSize: '14px' }}>{ net.name }</span>
                                </div>
                                <div style={{ fontSize: '13px', color: testnetMode ? '#ffaa33' : '#00e5a0', fontWeight: '600' }}>
                                    { testnetMode ? net.testnet : net.mainnet }
                                </div>
                            </div>
                        ) ) }
                    </div>
                </div>
            );

            case 'assets': return (
                <div className="cv-settings-content">
                    <h2>Manage Assets</h2>
                    <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
                        Assets you've hidden from your wallet's asset list appear here. Unhide any of them to bring them back.
                    </p>
                    { hiddenAssets.length === 0 ? (
                        <div className="cv-settings-card" style={{ textAlign: 'center', padding: '24px', color: '#888', fontSize: '13px' }}>
                            No hidden assets. Use the ⋮ menu on any asset to hide it.
                        </div>
                    ) : (
                        hiddenAssets.map( h => (
                            <div key={ h.id } className="cv-settings-card" style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    { h.icon && <img src={ h.icon } alt={ h.symbol } style={{ width: '28px', height: '28px', borderRadius: '50%' }} /> }
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '14px' }}>{ h.name || h.symbol }</div>
                                        <div style={{ fontSize: '12px', color: '#888', textTransform: 'capitalize' }}>{ h.network }</div>
                                    </div>
                                </div>
                                <button
                                    onClick={ () => unhideAsset( h.id ) }
                                    style={{
                                        background: 'rgba(108,99,255,0.2)', border: '1px solid rgba(108,99,255,0.4)',
                                        borderRadius: '20px', padding: '6px 16px', color: 'var(--cv-primary)',
                                        cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                                    }}
                                >
                                    Unhide
                                </button>
                            </div>
                        ) )
                    ) }
                </div>
            );

            case 'about': return (
                <div className="cv-settings-content">
                    <h2>About ChainVault</h2>

                    <div className="cv-settings-card" style={{ textAlign: 'center', padding: '32px 20px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '12px' }}>⛓</div>
                        <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--cv-text)', marginBottom: '4px' }}>ChainVault WP</div>
                        <div style={{ fontSize: '13px', color: '#888', marginBottom: '20px' }}>Version 1.0.0</div>
                        <div style={{ fontSize: '14px', color: '#aaa', lineHeight: '1.6', marginBottom: '20px' }}>
                            A professional multi-chain crypto wallet plugin for WordPress.
                            Supports Ethereum, Solana, BNB Chain, Polygon, Base and more.
                        </div>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            { [
                                { label: '🔒 Non-custodial', desc: 'You own your keys' },
                                { label: '⛓ Multi-chain',   desc: '5+ networks'       },
                                { label: '🔑 HD Wallet',     desc: 'BIP-39 standard'   },
                            ].map( ( item, i ) => (
                                <div key={ i } style={{
                                    background:   'rgba(255,255,255,0.04)',
                                    borderRadius: '10px',
                                    padding:      '12px 16px',
                                    textAlign:    'center',
                                    minWidth:     '100px',
                                }}>
                                    <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '4px' }}>{ item.label }</div>
                                    <div style={{ fontSize: '11px', color: '#888' }}>{ item.desc }</div>
                                </div>
                            ) ) }
                        </div>
                    </div>

                    <div className="cv-settings-card" style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '13px', color: '#888', lineHeight: '1.6' }}>
                            <strong style={{ color: 'var(--cv-text)' }}>⚠️ Important Disclaimer</strong><br />
                            ChainVault WP is a self-custodial wallet. We never store your private keys or seed phrases.
                            Always backup your seed phrase in a safe place. Lost seed phrases cannot be recovered.
                        </div>
                    </div>
                </div>
            );

            case 'referral': return (
                <div className="cv-settings-content">
                    <ReferralPage />
                </div>
            );

            default: return null;
        }
    };

    return (
        <div className="cv-page cv-settings-page">

            { /* Settings Sidebar */ }
            <div className="cv-settings-sidebar">
                <h2 className="cv-settings-sidebar-title">Settings</h2>
                { sections.map( s => (
                    <button
                        key={ s.id }
                        className={ 'cv-settings-tab ' + ( activeSection === s.id ? 'active' : '' ) }
                        onClick={ () => setActiveSection( s.id ) }
                    >
                        <span className="cv-settings-tab-icon">{ s.icon }</span>
                        <span className="cv-settings-tab-label">{ s.label }</span>
                    </button>
                ) ) }
            </div>

            { /* Settings Main Content */ }
            <div className="cv-settings-main">
                { renderSection() }
            </div>

        </div>
    );
}

export default SettingsPage;
